/**
 * Labels the server wrote for Home's suggestion chips
 * (`POST /api/amoreg/home/suggestion-labels`). The browser names each item by
 * its subject only; the server reads the title itself. Until labels land, and
 * whenever the request fails, a chip keeps its title label.
 *
 * @module state/homeSuggestionLabels
 */
import {
  HomeSuggestionLabelsResponse,
  HomeSuggestionSubject,
  type HomeSuggestionLabelsRequest,
} from "@amoreg/contracts";
import { homeSuggestionSubjectKey } from "@amoreg/shared/homeSuggestionLabels";
import { useAtomValue } from "@effect/atom-react";
import * as Effect from "effect/Effect";
import * as Option from "effect/Option";
import * as Schema from "effect/Schema";
import { AsyncResult, Atom } from "effect/unstable/reactivity";
import { useEffect, useMemo, useState } from "react";

import { resolvePrimaryEnvironmentHttpUrl } from "../environments/primary/target";

/**
 * Home's chips change while its reads land one by one; asking only once the
 * shown set holds still keeps labels from being written for chips shown for
 * a moment, and keeps a superseded ask from turning the settled one away.
 */
export const HOME_LABELS_SETTLE_MS = 600;

export async function requestHomeSuggestionLabels(
  organizationId: string,
  subjects: ReadonlyArray<HomeSuggestionSubject>,
  signal: AbortSignal,
): Promise<HomeSuggestionLabelsResponse> {
  const body: HomeSuggestionLabelsRequest = { organizationId, subjects };
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/api/amoreg/home/suggestion-labels", { organizationId }),
    {
      method: "POST",
      credentials: "include",
      signal: AbortSignal.any([signal, AbortSignal.timeout(25_000)]),
      headers: { "content-type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) throw new Error(`Home suggestion labels: ${response.status}`);
  return Schema.decodeUnknownSync(HomeSuggestionLabelsResponse)(await response.json());
}

const NO_LABELS: ReadonlyMap<string, string> = new Map();

export const WRITTEN_LABELS_REMEMBERED = 256;

/**
 * Every label written since the tab loaded, by organization, item and the
 * title it was written for, so a remount or a changed chip set shows it at
 * once instead of flashing back to the title label. The oldest is dropped
 * past the bound.
 */
const remembered = new Map<string, string>();
const memoryKey = (organizationId: string, subject: HomeSuggestionSubject, title: string) =>
  `${organizationId}\0${homeSuggestionSubjectKey(subject)}\0${title}`;

function remember(organizationId: string, subject: HomeSuggestionSubject, title: string, label: string): void {
  const key = memoryKey(organizationId, subject, title);
  remembered.delete(key);
  remembered.set(key, label);
  if (remembered.size > WRITTEN_LABELS_REMEMBERED) remembered.delete(remembered.keys().next().value!);
}

interface LabelsAsk {
  readonly organizationId: string;
  readonly subjects: ReadonlyArray<HomeSuggestionSubject>;
  /** The shown titles: never sent, they re-key the ask when an item is renamed and key the memory. */
  readonly titles: ReadonlyArray<string>;
}

/**
 * One request per ask, answered once: a failure is an empty answer for the
 * atom's life, never retried or polled. An ask names only the shown items the
 * memory lacks, so a failing server costs one request per distinct set of
 * them, however often Home renders or remounts.
 */
const labelsAtom = Atom.family((ask: string) => {
  const { organizationId, subjects, titles } = JSON.parse(ask) as LabelsAsk;
  const titleOf = new Map(subjects.map((subject, index) => [homeSuggestionSubjectKey(subject), titles[index]!]));
  return Atom.make(
    Effect.tryPromise((signal) => requestHomeSuggestionLabels(organizationId, subjects, signal)).pipe(
      Effect.map(({ labels }): ReadonlyMap<string, string> => {
        const written = new Map<string, string>();
        for (const { subject, label } of labels) {
          const key = homeSuggestionSubjectKey(subject);
          const title = titleOf.get(key);
          if (title === undefined) continue;
          remember(organizationId, subject, title, label);
          written.set(key, label);
        }
        return written;
      }),
      Effect.orElseSucceed(() => NO_LABELS),
    ),
  ).pipe(Atom.setIdleTTL(10 * 60_000), Atom.withLabel(`web-amoreg:home-suggestion-labels:${ask}`));
});

const noAskAtom = Atom.make(AsyncResult.success(NO_LABELS)).pipe(Atom.withLabel("web-amoreg:home-suggestion-labels:none"));

const isSubject = Schema.is(HomeSuggestionSubject);

type Shown = { readonly subject: HomeSuggestionSubject; readonly object: string };

/**
 * Subject key to written label for the shown suggestions: the remembered
 * ones at once, the others once asked about and answered; absent while the
 * answer is on its way and when it failed. Only items the memory lacks are
 * asked about. A subject the server would refuse (an id that is no UUID) is
 * left out, so it cannot fail the others.
 */
export function useWrittenLabels(organizationId: string | null, shown: ReadonlyArray<Shown>): ReadonlyMap<string, string> {
  const askable = useMemo(() => shown.filter(({ subject }) => isSubject(subject)), [shown]);
  const ask = useMemo(() => {
    if (!organizationId) return null;
    const unwritten = askable.filter(({ subject, object }) => !remembered.has(memoryKey(organizationId, subject, object)));
    return unwritten.length > 0
      ? JSON.stringify({
          organizationId,
          subjects: unwritten.map(({ subject }) => subject),
          titles: unwritten.map(({ object }) => object),
        } satisfies LabelsAsk)
      : null;
  }, [organizationId, askable]);
  const [settled, setSettled] = useState<string | null>(null);
  useEffect(() => {
    const timer = setTimeout(() => setSettled(ask), HOME_LABELS_SETTLE_MS);
    return () => clearTimeout(timer);
  }, [ask]);
  const current = ask !== null && ask === settled ? ask : null;
  const atom = useMemo(() => (current ? labelsAtom(current) : noAskAtom), [current]);
  const answer = Option.getOrElse(AsyncResult.value(useAtomValue(atom)), () => NO_LABELS);
  return useMemo(() => {
    if (!organizationId) return NO_LABELS;
    const written = new Map<string, string>();
    for (const { subject, object } of askable) {
      const key = homeSuggestionSubjectKey(subject);
      const label = remembered.get(memoryKey(organizationId, subject, object)) ?? answer.get(key);
      if (label !== undefined) written.set(key, label);
    }
    return written.size > 0 ? written : NO_LABELS;
  }, [organizationId, askable, answer]);
}
